// Strings of the deployment side of networking: Settings → Network, the Network card, the interruption banner, the
// after-apply callout, Destroy…, and an attached deployment's plan review (spec 11 6.15.14–6.15.16).
// `strings.networks.deployment`.
import type { TopologyKind } from '../../api/types';

export const networkDeployment = {
  // Settings → Network (6.15.14, mock n06).
  title: 'Network',
  standalone: 'Standalone',
  standaloneHelp: 'its own network per region',
  attach: 'Attach to a network',
  modeLegend: 'Network of the deployment',
  network: 'Network',
  chooseNetwork: 'Choose a network',
  networkOption: (name: string, topology: string, status: string, where: string) =>
    [name, topology, status, where].filter(Boolean).join(' · '),
  loadingNetworks: 'Loading networks…',
  noNetworks: 'No network exists yet: a network admin creates one under Networks.',
  reasons: {
    destroyed: (network: string) => `${network} is destroyed.`,
    adminsOnly: (network: string) => `${network} isn’t available: only network admins attach to it.`,
    changeNeedsDetach: (network: string, deployment: string) =>
      `Detach from ${network} first: ${deployment} can’t move between networks directly.`,
    retained: (deployment: string, network: string) =>
      `${deployment} keeps addresses of ${network}: release them and apply first.`,
  },
  size: 'Size',
  recommendedOption: (label: string) => `${label} · recommended`,
  recommendedLine: (deployment: string, size: string, role: string, need: number, capacity: number) =>
    `Recommended for ${deployment}: ${size} (${role} need about ${need} of ${capacity}).`,
  smallerWarning: (size: string, deployment: string, role: string, need: number, capacity: number) =>
    `${size} is smaller than ${deployment} needs: ${role} need about ${need} addresses and ${size} gives ${capacity}.`,
  advanced: 'Advanced',
  sizeIn: (provider: string, region: string) => `Size in ${provider} · ${region}`,
  sameAsAbove: 'Same as above',
  willGet: (deployment: string) => `Addresses ${deployment} will get`,
  addressesOf: (deployment: string) => `Addresses of ${deployment}`,
  columns: { where: 'Provider and region', block: 'Block', subnets: 'Subnets', result: 'Result' },
  willAttach: 'Will attach',
  staysStandalone: 'Stays standalone',
  notUsed: (network: string, provider: string) => `${network} doesn’t use ${provider}`,
  notIncluded: (network: string, region: string) => `${network} doesn’t include ${region}`,
  checking: 'Checking…',
  checkFailed: 'What attaching does couldn’t be checked.',
  whatAttaching: (deployment: string, regions: string) =>
    `Attaching re-creates ${deployment}’s network in ${regions}.`,
  whatResizing: (deployment: string, regions: string) =>
    `Resizing re-creates ${deployment}’s network in ${regions}.`,
  replaced: (resource: string, action: string) =>
    action === 'delete'
      ? `${resource} is deleted.`
      : action === 'replace'
        ? `${resource} is re-created on the new network.`
        : `${resource} moves to the new network.`,
  fewMinutes: 'The connect run usually takes a few minutes.',
  detachTitle: (network: string) => `Detach from ${network}`,
  detachText: (deployment: string, cidr: string) =>
    `${deployment} keeps ${cidr} as its own network; private name resolution pauses until Nebisys has re-created ${deployment}’s own DNS (a network run, then an apply of ${deployment}, both started now).`,
  detaching: (network: string) => `Detaching from ${network}:`,
  detachRun: 'detach run',
  thenApply: (deployment: string) => `then ${deployment}’s apply`,
  open: 'Open',
  openRunNamed: (name: string) => `Open ${name}`,
  retainedText: (deployment: string, cidr: string, network: string) =>
    `${deployment} keeps ${cidr} from ${network} as its own network.`,
  release: 'Release addresses…',
  releaseTitle: (deployment: string) => `Release ${deployment}’s addresses?`,
  releaseText: (deployment: string) =>
    `${deployment}’s next apply re-creates its standalone network: its apps and private endpoints move to new addresses, and private connections stop for a short time.`,
  releaseConfirm: 'Release',
  released: (deployment: string) => `${deployment}’s addresses are released.`,
  runActive: 'A run is active.',
  offReadOnly: (deployment: string) =>
    `Networking is switched off in this installation; ${deployment} keeps its network.`,
  mode: { attached: 'attached', retained: 'retained' },
  modeLabel: 'Mode',
  saveBar: {
    attaches: (deployment: string, network: string) => `attaches ${deployment} to ${network}`,
    detaches: (deployment: string, network: string) => `detaches ${deployment} from ${network}`,
    resizes: (deployment: string, network: string) => `changes ${deployment}’s size in ${network}`,
  },
  fieldName: 'network',
  readOnlyStandalone: (deployment: string) => `Standalone: ${deployment} has its own network.`,
  topology: {
    isolated: 'isolated',
    mesh: 'mesh',
    'hub-spoke': 'hub-spoke',
    'micro-segmentation': 'micro-segmentation',
  } satisfies Record<TopologyKind, string>,

  // The header's context line (6.4).
  contextNetwork: 'network',
  contextStandalone: 'standalone network',

  // The Network card on the Overview (6.15.15).
  card: 'Network',
  cardRegion: (provider: string, region: string) => `${provider} · ${region}`,
  blockAndSize: (cidr: string, size: string) => `${cidr} · ${size}`,
  standaloneLine: (deployment: string) => `Standalone: ${deployment} has its own network.`,
  attachInSettings: 'Attach it in Settings → Network',
  connected: 'Connected',
  since: (when: string) => `since ${when}`,
  connecting: 'Connecting',
  connectRun: 'connect run',
  failed: 'Failed',
  pending: 'Pending',
  applyToConnect: (deployment: string) => `Apply ${deployment} to connect`,
  spokeReady: 'Waiting for the connect run',
  detachingState: 'Detaching',
  ownNetwork: 'Own network with the same addresses',
  nameResolutionOff: (deployment: string) => `Private name resolution is off: apply ${deployment}`,
  askAdmin: (network: string) => `Ask a network admin to apply ${network}`,
  retry: 'Retry',
  planNetwork: 'Plan network',
  apply: 'Apply…',
  detach: 'Detach',
  retrySent: 'The connect run is queued.',

  // The interruption banner (6.15.15).
  interrupted: (when: string) => `Private connections interrupted since ${when}`,
  connectRunRunning: 'connect run running',
  connectRunFailed: 'connect run failed',
  connectRunQueued: 'connect run queued',

  // Destroy… of an attached deployment.
  destroyDetach: (deployment: string, network: string) =>
    `Nebisys first detaches ${deployment} from ${network} (a network run); the destroy waits for it and fails if the detach fails. ${deployment}’s addresses are released when the destroy completes.`,
  destroyOff: (deployment: string) =>
    `Networking is switched off: ${deployment}’s network resources must be detached first, which needs networking on.`,
};
